import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  Bell,
  Briefcase,
  Calendar,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  ClipboardCheck,
  Coins,
  Copy,
  CreditCard,
  Download,
  ListFilter,
  MapPin,
  Plug,
  ExternalLink,
  FileText,
  Flag,
  Globe,
  GraduationCap,
  History,
  Inbox,
  Info,
  LayoutDashboard,
  LayoutTemplate,
  Lightbulb,
  ListChecks,
  Lock,
  Mail,
  Megaphone,
  Menu as MenuIcon,
  MessageCircle,
  MessageSquare,
  MoreHorizontal,
  Package,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  ScrollText,
  Search,
  Send,
  Settings,
  Shield,
  Siren,
  Sparkle,
  Sparkles,
  SquarePen,
  Star,
  Target,
  ToggleLeft,
  Trash2,
  TrendingDown,
  Upload,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { ButtonHTMLAttributes } from 'react';
import { useId } from 'react';
import { siInstagram, siMessenger, siWhatsapp } from 'simple-icons';
import type { Channel } from './basics.js';
import { cx } from './cx.js';

/**
 * One icon set, Lucide, 2px stroke (ADR 126, D13). The map is curated so an app
 * never reaches for an icon the product has not agreed on. The nine mockup names
 * (bell, briefcase, chart-line, dashboard, inbox, message, package, settings, star)
 * keep working.
 */
export const ICONS = {
  // navigation
  dashboard: LayoutDashboard,
  inbox: Inbox,
  calendar: Calendar,
  tasks: CheckSquare,
  leads: Target,
  contacts: Users,
  feedback: Flag,
  services: Sparkles,
  products: Package,
  teach: GraduationCap,
  marketing: Megaphone,
  reports: BarChart3,
  settings: Settings,
  profile: Users,
  clinics: Briefcase,
  approvals: ClipboardCheck,
  review: Shield,
  monitoring: BarChart3,
  governance: Lock,
  billing: CreditCard,
  health: Activity,
  cost: Coins,
  templates: LayoutTemplate,
  audit: ScrollText,
  flags: ToggleLeft,
  incidents: Siren,
  terms: FileText,
  checklist: ListChecks,
  'trend-down': TrendingDown,
  // channels
  whatsapp: MessageCircle,
  sms: MessageSquare,
  instagram: MessageSquare,
  messenger: MessageSquare,
  webchat: Globe,
  phone: Phone,
  email: Mail,
  // actions and states
  add: Plus,
  edit: Pencil,
  delete: Trash2,
  close: X,
  check: Check,
  warning: AlertTriangle,
  info: Info,
  help: CircleHelp,
  idea: Lightbulb,
  search: Search,
  send: Send,
  copy: Copy,
  download: Download,
  upload: Upload,
  refresh: RefreshCw,
  external: ExternalLink,
  more: MoreHorizontal,
  menu: MenuIcon,
  bell: Bell,
  star: Star,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'chevron-down': ChevronDown,
  'chevron-right': ChevronRight,
  'chevron-left': ChevronLeft,
  filter: ListFilter,
  'map-pin': MapPin,
  plug: Plug,
  // ADR 252: a feature the plan does not include.
  lock: Lock,
  sort: ArrowUpDown,
  // Ask Laura (ADR 240, D57)
  ask: Sparkle,
  history: History,
  'new-chat': SquarePen,
  // mockup names (aliases)
  briefcase: Briefcase,
  'chart-line': BarChart3,
  message: MessageSquare,
  package: Package,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICONS;

/**
 * Decorative by default: always next to a word. Give `label` only when the icon
 * is the only thing that carries the meaning (a status cell, for example).
 */
export function Icon({
  name,
  label,
  size = 20,
  className,
}: {
  name: IconName;
  label?: string;
  size?: 16 | 20 | 24;
  className?: string;
}) {
  const Glyph = ICONS[name];
  return (
    <Glyph
      className={cx('icon', className)}
      width={size}
      height={size}
      strokeWidth={2}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
      focusable="false"
    />
  );
}

/**
 * The brand marks people know a channel by (ADR 183, D42): WhatsApp, Instagram
 * and Messenger glyphs from `simple-icons` (CC0; the marks themselves belong to
 * their owners and are used only to say which channel a message came from, at
 * the brand's own colour and proportions). SMS, website chat and phone keep
 * their Lucide icons. Decorative by default: the channel's name is always
 * beside it (`ChannelBadge`); `label` makes it the carrier of meaning.
 */
const BRAND_PATHS: Partial<Record<Channel, string>> = {
  whatsapp: siWhatsapp.path,
  instagram: siInstagram.path,
  messenger: siMessenger.path,
};
const CHANNEL_LUCIDE: Partial<Record<Channel, LucideIcon>> = {
  sms: MessageSquare,
  webchat: Globe,
  voice: Phone,
};

export function ChannelIcon({
  channel,
  label,
  size = 14,
  className,
}: {
  channel: Channel;
  label?: string;
  size?: 12 | 14 | 16 | 20;
  className?: string;
}) {
  const gradientId = useId();
  const a11y = label
    ? ({ role: 'img', 'aria-label': label } as const)
    : ({ 'aria-hidden': true } as const);
  const path = BRAND_PATHS[channel];
  if (path) {
    const gradient = channel === 'instagram';
    return (
      <svg
        className={cx('icon ch-icon', `ch-icon-${channel}`, className)}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={gradient ? `url(#${gradientId})` : 'currentColor'}
        focusable="false"
        {...a11y}
      >
        {gradient && (
          <defs>
            <linearGradient id={gradientId} x1="0" y1="1" x2="1" y2="0">
              <stop offset="0" className="ig-stop-1" />
              <stop offset="0.45" className="ig-stop-2" />
              <stop offset="0.75" className="ig-stop-3" />
              <stop offset="1" className="ig-stop-4" />
            </linearGradient>
          </defs>
        )}
        <path d={path} />
      </svg>
    );
  }
  const Glyph = CHANNEL_LUCIDE[channel] ?? MessageSquare;
  return (
    <Glyph
      className={cx('icon ch-icon', `ch-icon-${channel}`, className)}
      width={size}
      height={size}
      strokeWidth={2}
      focusable="false"
      {...a11y}
    />
  );
}

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label'
> {
  icon: IconName;
  /** What the button does, read by screen readers and shown as a tooltip. */
  label: string;
  variant?: 'default' | 'danger' | 'ghost';
  small?: boolean;
}

/** A button that is only an icon. The label is required so it is never a mystery (D13). */
export function IconButton({
  icon,
  label,
  variant = 'default',
  small,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      className={cx('iconbtn', variant !== 'default' && variant, small && 'small', className)}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Icon name={icon} />
    </button>
  );
}
